import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import MaterialTable from '@material-table/core';
import { CsvBuilder } from 'filefy';
import './Table.css';

const ReportsTable = ({ tableName, tableIndex, columns, dataSource, loading, sliceName }) => {
    // Get the specific data based on dataSource prop and sliceName
    const data = useSelector(state => {
        const slice = state[sliceName];
        if (!slice) return [];
        switch (dataSource) {
            case 'loadData':
                return slice.loadData?.data || [];
            case 'skipData':
                return slice.skipData?.data || [];
            case 'manualData':
                return slice.manualData?.data || [];
            case 'compoundData':
                return slice.compoundData?.data || [];
            // Added MMT2 aggregate datasets
            case 'agg1Data':
                return slice.agg1Data?.data || [];
            case 'agg2Data':
                return slice.agg2Data?.data || [];
            // Added COCA Annual datasets
            case 'COCAByScenarioData':
                return slice.COCAByScenarioData?.data || [];
            // coca Annual 2 annual report
            case 'cocaAnnualReport':
                return slice.annualReportData?.data || [];
            default:
                return [];
        }
    });

    // Create a deep copy of the data to avoid read-only property issues
    const tableData = useMemo(() => {
        if (!data || !Array.isArray(data)) {
            return [];
        }

        // Method 1: Deep clone with spread operator and Object.assign
        try {
            return data.map((item, index) => {
                const newItem = {};
                Object.keys(item || {}).forEach(key => {
                    newItem[key] = item[key];
                });
                // Add a unique tableData property that MaterialTable can modify
                newItem.tableData = { id: index };
                return newItem;
            });
        } catch (error) {
            console.error('Error processing table data:', error);
            return [];
        }
    }, [data]);

    const exportCsv = (columns, data) => {
        const columnTitles = columns.map(columnDef => columnDef.title);
        const csvData = data.map(rowData => columns.map(columnDef => rowData[columnDef.field]));
        new CsvBuilder('data.csv')
            .setColumns(columnTitles)
            .addRows(csvData)
            .exportFile();
    }

    // Create a mutable copy of columns to prevent read-only errors
    const mutableColumns = useMemo(() => {
        if (!columns || !Array.isArray(columns)) {
            return [];
        }

        try {
            return columns.map((col, index) => {
                // Create a completely new column object
                const newCol = {};
                Object.keys(col).forEach(key => {
                    if (key === 'cellStyle' && typeof col[key] === 'object') {
                        // Deep copy cellStyle object
                        newCol[key] = { ...col[key] };
                    } else {
                        newCol[key] = col[key];
                    }
                });

                // Ensure column has required properties
                if (!newCol.field) {
                    newCol.field = `field_${index}`;
                }
                if (!newCol.title) {
                    newCol.title = `Column ${index + 1}`;
                }

                return newCol;
            });
        } catch (error) {
            console.error('Error processing columns:', error);
            return [];
        }
    }, [columns]);

    return (
        (columns.length > 0) &&
        <div style={{ paddingTop: '24px' }}>
            <MaterialTable
                columns={mutableColumns}
                data={tableData}
                isLoading={loading}
                title={tableName}
                options={{
                    maxColumnSort: true,
                    pageSize: 25,
                    maxBodyHeight: '100%',
                    paginationType: 'stepped',
                    overflowY: 'visible',
                    paginationPosition: 'bottom',
                    pageSizeOptions: [25, 50, 100],
                    emptyRowsWhenPaging: false,
                    totalCount: true,
                    rowStyle: {
                        fontSize: 'var(--font-size-small-header)',
                        fontFamily: "'optum sans'",
                        border: '1px solid rgba(224, 224, 224, 1)',
                        textAlign: 'center'

                    },
                    headerStyle: {
                        fontSize: 'var(--font-size-table)',
                        fontWeight: 700,
                        fontFamily: "'optum sans'",
                        padding: '6px',
                        border: '1px solid rgb(68, 68, 68)',
                        borderTop: '.2em solid rgb(90,90,90)',
                        position: 'sticky',
                        top: 0,
                        backgroundColor: 'var(--primary-color)',
                        color: '#000C44',
                        textAlign: 'center',
                    },
                    search: false,
                    setRowsPerPage: false,
                    exportMenu: [
                        {
                            label: 'Export CSV',
                            exportFunc: (cols, datas) => exportCsv(cols, datas),
                        }
                    ],
                }}
            />
        </div>

    );
};

export default ReportsTable;